import { Controller } from "@hotwired/stimulus"
import { readChartTheme, observeChartTheme } from "../utils/chart_theme"
import { legendLabelsForScaleConfig, scaleConfigForValues } from "../utils/france_departments_map_scale"

const CHART_HEIGHT = 420
const METROPOLITAN_FRANCE_BOUNDS = [-4.6, 41.25, 9.5, 51.15]
const PRIMARY_PALETTE_ID = "depozen-primary"
const MOBILE_LAYOUT_MEDIA = "(max-width: 991.98px)"

let apexMapsModulePromise = null

function loadApexMaps() {
  if (!apexMapsModulePromise) {
    apexMapsModulePromise = Promise.all([
      import("apexmaps"),
      import("apexmaps/apexmaps.css")
    ]).then(([apexMapsModule]) => apexMapsModule.default)
  }

  return apexMapsModulePromise
}

function parseHex(hex) {
  const normalized = hex.replace("#", "")
  const value = normalized.length === 3
    ? normalized.split("").map((char) => char + char).join("")
    : normalized

  return {
    r: Number.parseInt(value.slice(0, 2), 16),
    g: Number.parseInt(value.slice(2, 4), 16),
    b: Number.parseInt(value.slice(4, 6), 16)
  }
}

function toHex({ r, g, b }) {
  const channel = (value) => Math.max(0, Math.min(255, Math.round(value))).toString(16).padStart(2, "0")
  return `#${channel(r)}${channel(g)}${channel(b)}`
}

function mixHex(from, to, amount) {
  const start = parseHex(from)
  const end = parseHex(to)
  const ratio = Math.max(0, Math.min(1, amount))

  return toHex({
    r: start.r + (end.r - start.r) * ratio,
    g: start.g + (end.g - start.g) * ratio,
    b: start.b + (end.b - start.b) * ratio
  })
}

// One stop per legend class (see LEGEND_CLASSES in france_departments_map_scale).
// Extra stops made the top Jenks tier pick a darker color than the tier below on dark UI.
function buildPrimaryStops(primaryHex, theme) {
  if (theme.isDark) {
    return [
      mixHex(theme.mapNullColor, primaryHex, 0.2),
      mixHex(theme.mapNullColor, primaryHex, 0.42),
      mixHex(theme.mapNullColor, primaryHex, 0.64),
      mixHex(primaryHex, "#ffffff", 0.22)
    ]
  }

  return [
    mixHex(primaryHex, "#ffffff", 0.78),
    mixHex(primaryHex, "#ffffff", 0.5),
    mixHex(primaryHex, "#ffffff", 0.22),
    mixHex(primaryHex, "#000000", 0.18)
  ]
}

function registerPrimaryPalette(ApexMaps, primaryHex, theme) {
  ApexMaps.registerPalette(PRIMARY_PALETTE_ID, {
    kind: "sequential",
    stops: buildPrimaryStops(primaryHex, theme)
  })
}

function formatCount(value) {
  if (value == null || Number.isNaN(value)) return "—"
  return Math.round(value).toLocaleString("fr-FR")
}

function isPassiveTouchLayout() {
  return document.body.classList.contains("turbo-native") ||
    window.matchMedia(MOBILE_LAYOUT_MEDIA).matches
}

export default class extends Controller {
  static targets = ["chart"]
  static values = { payload: Object }

  connect() {
    this.renderToken = 0
    this.boundResize = () => this.resizeChart()
    this.boundPassiveLayoutChange = () => this.renderChart()
    this.passiveLayoutMediaQuery = window.matchMedia(MOBILE_LAYOUT_MEDIA)
    this.passiveLayoutMediaQuery.addEventListener("change", this.boundPassiveLayoutChange)
    this.unobserveTheme = observeChartTheme(() => this.renderChart())
    window.addEventListener("resize", this.boundResize)
    this.rafId = requestAnimationFrame(() => {
      this.rafId = requestAnimationFrame(() => this.renderChart())
    })
  }

  disconnect() {
    // Invalide un rendu encore en vol : l'élément quitte le document, rien ne doit s'y monter.
    this.renderToken++
    if (this.rafId) cancelAnimationFrame(this.rafId)
    this.passiveLayoutMediaQuery?.removeEventListener("change", this.boundPassiveLayoutChange)
    this.unobserveTheme?.()
    window.removeEventListener("resize", this.boundResize)
    if (this.resizeObserver) {
      this.resizeObserver.disconnect()
      this.resizeObserver = null
    }
    this.destroyChart()
  }

  async renderChart() {
    if (!this.hasChartTarget) return

    const departments = this.payloadValue?.departments
    if (!departments || Object.keys(departments).length === 0) return

    // `renderChart` franchit un `await` avant de monter quoi que ce soit. Sans jeton, deux
    // appels rapprochés — bascule de thème, passage en layout tactile, double rAF du connect —
    // traversent tous les deux un `destroyChart` qui ne voit encore aucune instance, et montent
    // chacun leur carte. Seul le rendu le plus récent a le droit d'aller au bout.
    const token = ++this.renderToken

    try {
      const ApexMaps = await loadApexMaps()
      if (token !== this.renderToken || !this.hasChartTarget) return

      this.destroyChart()
      // ApexMaps empile ses nœuds dans le conteneur (`appendChild`) et ne le vide jamais, et
      // `destroyChart` ne sait défaire que l'instance montée par CE contrôleur. Au retour sur
      // le tableau de bord — seule page gardée en snapshot Turbo, et dont le corps est une
      // frame paresseuse greffée d'une visite à l'autre — la carte de la visite précédente
      // revient dans le DOM sans plus personne pour la posséder : sans ce vidage, elle reste
      // en place et la nouvelle se pose en dessous, une de plus à chaque aller-retour.
      this.chartTarget.replaceChildren()

      const theme = readChartTheme()
      const primaryHex = this.payloadValue?.colors?.primary || "#577ebf"
      registerPrimaryPalette(ApexMaps, primaryHex, theme)

      const data = Object.entries(departments).map(([code, value]) => ({
        code,
        value: Number(value)
      }))
      const scaleValues = data.map((point) => point.value)
      const scaleConfig = scaleConfigForValues(scaleValues)
      const legendLabels = legendLabelsForScaleConfig(scaleValues, scaleConfig, formatCount)
      const passiveTouch = isPassiveTouchLayout()

      this.element.classList.toggle("france-departments-heatmap--passive-touch", passiveTouch)

      const labels = this.payloadValue.labels || {}
      const hoverState = passiveTouch
        ? { brightness: 1, stroke: theme.mapStrokeColor, strokeWidth: 1.1 }
        : {
          brightness: theme.isDark ? 1.08 : 0.92,
          stroke: theme.mapStrokeColor,
          strokeWidth: 1.4
        }

      const mapInstance = new ApexMaps(this.chartTarget, {
        chart: {
          height: CHART_HEIGHT,
          fontFamily: "inherit",
          background: "transparent"
        },
        geo: {
          map: "fr/admin1@10m",
          projection: "mercator",
          view: { fit: METROPOLITAN_FRANCE_BOUNDS, padding: { top: 0, right: 2, bottom: 4, left: 2 } },
          sphere: { show: false },
          graticule: { show: false }
        },
        interaction: {
          zoom: { enabled: false, wheel: false, controls: false },
          pan: { enabled: false },
          rotate: { enabled: false },
          selection: { enabled: !passiveTouch }
        },
        series: [{
          name: labels.series || "Contrats",
          joinBy: ["iso_3166_2", "code"],
          data,
          stroke: {
            color: theme.mapStrokeColor,
            width: 1.1
          },
          scale: {
            ...scaleConfig,
            palette: PRIMARY_PALETTE_ID,
            nullColor: theme.mapNullColor
          },
          states: {
            hover: hoverState
          }
        }],
        legend: {
          title: labels.legend || labels.series,
          position: "bottom",
          align: "center",
          style: "classes",
          showNull: false,
          marker: false,
          interactive: !passiveTouch,
          formatter: (item, index) => {
            if (item.isNull) return ""

            if (item.from == null && item.to == null) return item.label

            return legendLabels[index] ?? formatCount(item.to ?? item.from)
          }
        },
        tooltip: {
          enabled: !passiveTouch,
          formatter: ({ name, value }) => {
            const countLabel = labels.contracts || "contrats"
            const displayValue = value == null
              ? (labels.no_data || "Aucune donnée")
              : `${formatCount(value)} ${countLabel}`

            return `<div style="font-weight:600;margin-bottom:2px">${name || ""}</div>` +
              `<div style="font-variant-numeric:tabular-nums;font-size:15px;font-weight:600">${displayValue}</div>`
          }
        },
        a11y: { enabled: true, dataTable: false }
      })

      this.mapInstance = mapInstance
      // Le rendu d'ApexMaps est lui aussi asynchrone : un rendu plus récent a pu démonter
      // celui-ci entre-temps, inutile d'aller brancher des observateurs sur un mort.
      await mapInstance.render()
      if (token !== this.renderToken) return

      this.observeResize()
      this.resizeChart()
      this.triggerMasonryRelayout()
    } catch (error) {
      console.error("[france-departments-map] render failed", error)
    }
  }

  triggerMasonryRelayout() {
    this.element.closest("[data-controller~='masonry']")?.dispatchEvent(new Event("resize"))
  }

  resizeChart() {
    if (!this.mapInstance || !this.hasChartTarget) return
    if (this.chartTarget.clientWidth === 0) return
    this.mapInstance.resize?.()
  }

  observeResize() {
    if (typeof ResizeObserver === "undefined" || !this.hasChartTarget) return
    this.resizeObserver = new ResizeObserver(() => {
      this.resizeChart()
      this.triggerMasonryRelayout()
    })
    this.resizeObserver.observe(this.chartTarget)
  }

  destroyChart() {
    if (!this.mapInstance) return
    this.mapInstance.destroy?.()
    this.mapInstance = null
  }
}
